import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import styled from 'styled-components';
import { AnimatedStepBody } from '../animation/StepBodyAnimations';
import { getPostAccountCreationSteps } from '../store/flow/selectors';
import { devices } from 'signup-constants/styles/viewports.styles';
import { GreetingsPretitle } from '../views-components/GreetingsPretitle';
import { StepSubtitle } from '../views-components/StepSubtitle';
import { StepTitle } from '../views-components/StepTitle';
import { Step } from 'signup-constants/Step';
import { LayoutTemplateBelowTitleContainer, LayoutTemplateContentContainer } from '../app/AppViewComponent.styles';
import { useLayoutTemplate } from '../hooks/useLayoutTemplate';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { useIndustry } from '../step-industry/useIndustry';
import { useCurrentStep } from '../hooks/useCurrentStep';
import { getFlowConfig } from '../flow-initial/getFlowConfig';
import { getStepConfig } from './getStepConfig';
import { LayoutType } from 'signup-components/types';
import { SidePanelBaseView } from './side-panel/SidePanelBaseView';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BaseViewHeader = styled.header.withConfig({
  displayName: "BaseView__BaseViewHeader"
})(["margin-bottom:", ";", " @media ", "{", "}"], ({
  theme
}) => theme.space['600'], props => !props.$isPostAccountCreationStep && `text-align: center;`, devices.desktop, props => props.$stepKey === Step.HubletSelection && `margin-bottom: 0;`);
const BaseViewHelperText = styled.div.withConfig({
  displayName: "BaseView__BaseViewHelperText"
})(["", " ", " ", ""], props => ![Step.CrmExperienceLevel].includes(props.$stepKey) && `text-align: left;`, props => !props.$isPostAccountCreationStep && `text-align: center;`, props => props.$stepKey === Step.HubletSelection && `text-align: left !important; `);

/**
 * @param hideSidePanelNextButton - Enables the next button in the footer to be hidden on buttons views where the user does not need to click next to get to the next step
 */
export const BaseView = ({
  title,
  helperText,
  children,
  stepKey,
  navMarker,
  preTitleMessage = '',
  addExperimentClassName = false,
  disableAnimation = false,
  nextButtonLabel,
  hideSidePanelNextButton = false
}) => {
  const {
    currentStep
  } = useCurrentStep();
  const step = stepKey || currentStep;
  const {
    layoutType
  } = useLayoutTemplate();
  const stepConfig = getStepConfig(step);
  const {
    clickedToSeeAll,
    predictedIndustries
  } = useIndustry();
  const predictions = predictedIndustries.length > 0;
  const postAccountCreation = useSelector(getPostAccountCreationSteps);
  if (!step) {
    return null;
  }
  const shouldRestrictWidth = stepConfig && stepConfig.shouldRestrictWidth;
  const isPostAccountCreationStep = postAccountCreation.includes(currentStep);
  const shouldUseWave = getFlowConfig().shouldUseWaveOnPostAccountCreation;
  const stepTitle = /*#__PURE__*/_jsx(StepTitle, {
    stepKey: step,
    updateStyling: !isPostAccountCreationStep
  });
  const navMarkerName = navMarker || `${step}_VIEW`;
  const experimentSeleniumAttribute = addExperimentClassName ? {
    'data-selenium-experiment': 'EXPERIMENT'
  } : null;
  const getPretitle = () => {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(GreetingsPretitle, {
        hasPreTitle: isValidI18nKey(preTitleMessage)
      }), /*#__PURE__*/_jsx(StepSubtitle, {
        message: preTitleMessage
      })]
    });
  };
  if (layoutType === LayoutType.SidePanel) {
    return /*#__PURE__*/_jsx(SidePanelBaseView, {
      addExperimentClassName: addExperimentClassName,
      disableAnimation: disableAnimation,
      helperText: helperText,
      hideSidePanelNextButton: hideSidePanelNextButton,
      navMarker: navMarker,
      nextButtonLabel: nextButtonLabel,
      stepKey: step,
      title: title,
      children: children
    });
  }
  return /*#__PURE__*/_jsxs(AnimatedStepBody, {
    dataStep: step,
    disableAnimation: disableAnimation,
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: navMarkerName
    }), /*#__PURE__*/_jsxs("div", Object.assign({
      "data-selenium": `${step}_VIEW`
    }, experimentSeleniumAttribute, {
      children: [/*#__PURE__*/_jsxs(BaseViewHeader
      // @ts-expect-error Expression produces a union type that is too complex to represent.
      , {
        $isPostAccountCreationStep: isPostAccountCreationStep && shouldUseWave,
        $stepKey: step,
        children: [getPretitle(), title || stepTitle, /*#__PURE__*/_jsx(LayoutTemplateBelowTitleContainer, {
          $isPostAccountCreationStep: isPostAccountCreationStep && shouldUseWave,
          $shouldRestrictWidth: shouldRestrictWidth,
          children: /*#__PURE__*/_jsx(BaseViewHelperText, {
            $isPostAccountCreationStep: isPostAccountCreationStep && shouldUseWave,
            $stepKey: step,
            children: helperText
          })
        })]
      }), /*#__PURE__*/_jsx(LayoutTemplateContentContainer, {
        $clickedToSeeAll: clickedToSeeAll || predictions,
        $layoutType: layoutType,
        $shouldRestrictWidth: shouldRestrictWidth,
        $stepKey: step,
        children: children
      })]
    }))]
  });
};